import React from 'react';
import styled from 'styled-components';
import { Box } from '@mui/material';
import { useFormik } from 'formik';
import { string, object } from 'yup';
import { frcDecisionOptions, FrcsbDecision } from './options';
import { PAGE_TEXT } from '../../../lang';
import {
  type FrcAssessment,
  type DrcAdmissionInfo,
} from '../../../graphql/FRCAssessment';
import { Select, Button, TextField } from '../../../../../components';

type FRCAssessmentBoxProps = {
  initialValues: FrcAssessment;
  isViewMode: boolean;
  drcAdmissionValues: DrcAdmissionInfo;
  onSubmit: (values: FrcAssessment) => void;
};

export const FRCAssessmentBox: React.FunctionComponent<
  FRCAssessmentBoxProps
> = props => {
  const { initialValues, isViewMode, drcAdmissionValues, onSubmit } = props;
  const STEP_TEXT = PAGE_TEXT.FRC_ASSESSMENT_DETAILS.ASSESSMENT;

  const { entranceRequirementWaive, confirmationChairInTeam } =
    drcAdmissionValues;

  // situation: frc return to drsc, changes made by dsrc may make the frc approval not required
  const frcRequired = entranceRequirementWaive || confirmationChairInTeam;

  const formik = useFormik<FrcAssessment>({
    initialValues,
    validationSchema: object({
      approveDsrcChairInTeam: string().when([], {
        is: () => confirmationChairInTeam,
        then: schema =>
          schema.required('Required').notOneOf([FrcsbDecision.NA], 'Required'),
        otherwise: schema => schema.notRequired(),
      }),
      approveWaivingEntranceRequirements: string().when([], {
        is: () => entranceRequirementWaive,
        then: schema =>
          schema.required('Required').notOneOf([FrcsbDecision.NA], 'Required'),
        otherwise: schema => schema.notRequired(),
      }),
      remarks: string().when(
        ['approveDsrcChairInTeam', 'approveWaivingEntranceRequirements'],
        {
          is: (
            approveDsrcChairInTeam: string,
            approveWaivingEntranceRequirements: string,
          ) =>
            approveDsrcChairInTeam === FrcsbDecision.RETURN ||
            approveWaivingEntranceRequirements === FrcsbDecision.RETURN,
          then: schema => schema.required('Required'),
          otherwise: schema => schema.notRequired(),
        },
      ),
    }),
    onSubmit,
  });

  const handleChangeApproval = async (
    event: React.ChangeEvent<{ value: unknown }>,
    field: string,
  ): Promise<void> => {
    const { value } = event.target;
    if (value !== FrcsbDecision.RETURN)
      await formik.setFieldValue('remarks', '');

    await formik.setFieldValue(field, value);
  };

  const needRemark =
    formik.values.approveWaivingEntranceRequirements === FrcsbDecision.RETURN ||
    formik.values.approveDsrcChairInTeam === FrcsbDecision.RETURN;

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      {frcRequired ? (
        <ContentBox>
          <Box className="w-full" padding={2}>
            <div className="mb-4">
              <h3 className="font-bold mb-2">{STEP_TEXT.DRC_CHAIR.TITLE}</h3>
              <div className="insert-selection-area">
                <span className="flex-container">
                  <p className="mt-[1.25rem]">{STEP_TEXT.DRC_CHAIR.OPTION_0}</p>
                  <Select
                    id="approveDsrcChairInTeam"
                    name="approveDsrcChairInTeam"
                    disabled={isViewMode || !confirmationChairInTeam}
                    options={frcDecisionOptions}
                    value={formik.values.approveDsrcChairInTeam}
                    InputLabelProps={{ shrink: true }}
                    onChange={async e => {
                      await handleChangeApproval(e, 'approveDsrcChairInTeam');
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      Boolean(formik.touched.approveDsrcChairInTeam) &&
                      Boolean(formik.errors.approveDsrcChairInTeam)
                    }
                    helperText={
                      Boolean(formik.touched.approveDsrcChairInTeam) &&
                      formik.errors.approveDsrcChairInTeam
                    }
                    sx={{
                      width: '12rem',
                      margin: '0.25rem 0.5rem',
                      textAlign: 'center',
                      background: confirmationChairInTeam
                        ? 'inherit'
                        : '#E6E6E6',
                    }}
                  />
                  <p className="mt-[1.25rem]">{STEP_TEXT.DRC_CHAIR.OPTION_1}</p>
                </span>
              </div>
            </div>
          </Box>
          <Box className="w-full" padding={2}>
            <div className="mb-4">
              <h3 className="font-bold mb-2">{STEP_TEXT.REQUIREMENTS.TITLE}</h3>
              <div className="insert-selection-area">
                <span className="flex-container">
                  <p className="mt-[1.25rem]">
                    {STEP_TEXT.REQUIREMENTS.OPTION_0}
                  </p>
                  <Select
                    id="approveWaivingEntranceRequirements"
                    name="approveWaivingEntranceRequirements"
                    disabled={isViewMode || !entranceRequirementWaive}
                    options={frcDecisionOptions}
                    value={formik.values.approveWaivingEntranceRequirements}
                    InputLabelProps={{ shrink: true }}
                    onChange={async e => {
                      await handleChangeApproval(
                        e,
                        'approveWaivingEntranceRequirements',
                      );
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      Boolean(
                        formik.touched.approveWaivingEntranceRequirements,
                      ) &&
                      Boolean(formik.errors.approveWaivingEntranceRequirements)
                    }
                    helperText={
                      Boolean(
                        formik.touched.approveWaivingEntranceRequirements,
                      ) && formik.errors.approveWaivingEntranceRequirements
                    }
                    sx={{
                      width: '12rem',
                      margin: '0.25rem 0.5rem',
                      textAlign: 'center',
                      background: entranceRequirementWaive
                        ? 'inherit'
                        : '#E6E6E6',
                    }}
                  />
                  <p className="mt-[1.25rem]">
                    {STEP_TEXT.REQUIREMENTS.OPTION_1}
                  </p>
                </span>
              </div>
            </div>
          </Box>
          <Box className="w-full" padding={2}>
            {needRemark && (
              <>
                <h2 className="font-bold">{STEP_TEXT.REMARKS_LABEL}</h2>
                <TextField
                  className="w-full !py-2 !pl-4"
                  id="remarks"
                  name="remarks"
                  disabled={isViewMode}
                  value={formik.values.remarks}
                  InputLabelProps={{ shrink: true }}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    Boolean(formik.touched.remarks) &&
                    Boolean(formik.errors.remarks)
                  }
                  helperText={
                    Boolean(formik.touched.remarks) && formik.errors.remarks
                  }
                  multiline
                  label={''}
                  minRows={3}
                />
              </>
            )}
          </Box>
        </ContentBox>
      ) : (
        <div className="p-4">
          <h2 className="font-semibold">{STEP_TEXT.NOT_REQUIRED_1}</h2>
          {!isViewMode && (
            <h2 className="mt-2 font-bold text-[#951d1d]">
              {STEP_TEXT.NOT_REQUIRED_2}
            </h2>
          )}
        </div>
      )}

      {!isViewMode && (
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          marginY={4}
        >
          <Button variant="contained" type="submit">
            {PAGE_TEXT.SUBMIT}
          </Button>
        </Box>
      )}
    </form>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  margin: 0.5rem 1rem 0;

  .flex-container {
    display: flex;
    align-items: flex-start;
  }

  .insert-selection-area {
    vertical-align: middle;
    margin-left: 1.5rem;
    margin-bottom: 0.5rem;
  }
`;
